import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  KeyEventData,
  KeyEventDataTooltip,
  keyEventPathData,
  TimelineAnimationState,
  TimelineAxisTick,
  TimelineCanvas,
  TimelineChartLine,
  TimelineData,
  TimelineDataWithXY,
  TimelineStep,
  TimelineTooltip,
  TimelineTooltipAlign
} from './timeline-model';

import { getStartOfTheDayAsDate } from './helper/time-helper';
import { TimelineUnitConfig, timeUnitConfig } from './helper/time-unit-config';

import { CommonModule, DatePipe } from '@angular/common';
import { LoaderModule } from '@innovation-os-ng/ui/loader';
import { Ui2EmptyStateModule } from '@innovation-os-ng/ui/ui2-empty-state';
import { FormatNumPipe, TitleCasePipe } from '@innovation-os-ng/ui/utils';
import { ColorPalettes, ThemeService } from '@innovation-os-ng/ui/viz-tools-ng';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import * as d3 from 'd3';
import { clearTimeAndReturnDate } from '../../helpers/chartHelpers';
import { LineChartComponent } from './line-chart/line-chart.component';
import { TimelineRangeSelectorComponent } from './timeline-range-selector/timeline-range-selector.component';

enum CategoryMapType {
  TYPE = 'type',
  COUNTRY = 'country'
}

@Component({
  selector: 'chart-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LoaderModule,
    FormatNumPipe,
    LineChartComponent,
    TimelineRangeSelectorComponent,
    TitleCasePipe,
    Ui2EmptyStateModule
  ]
})
export class TimelineUiComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private readonly host: ElementRef,
    private readonly themeService: ThemeService,
    private readonly datePipe: DatePipe
  ) {}

  /**
   * INPUTS
   */

  @Input()
  public range: Array<string>;

  @Input()
  private categoryKey: string;

  @Input()
  public noData: boolean;

  @Input()
  public loading: boolean;

  @Input()
  public noDataTimeframe: boolean;

  /**
   * Get the key event duration path data.
   */
  @Input()
  public keyEventPathData: Record<string, Array<keyEventPathData>> = {};

  /**
   * We always use same data range when normalized data
   * is shown in timeline.
   * So we want to provide data from parent.
   *
   * Note: For normalized data we always
   * want the data range to be [0,100]
   */
  @Input()
  public normalizedDataRange: Array<number>;

  /**
   * Receives the flag that indicates the normalized process
   * is still running.
   */
  @Input()
  public isNormalizeProcessStillRunning: boolean;

  @Input()
  public isOldElement: boolean;

  @Input()
  public showLegend: boolean;

  /**
   * Flag to control the y axis visibility.
   */
  @Input()
  public showYAxis: boolean = true;

  /**
   * Key events data points
   */
  @Input()
  public keyEventsData: Array<KeyEventData> = [];

  @Input()
  public set selectKeyEvent(keyEvent: KeyEventData) {
    if (!keyEvent) {
      /**
       * If we have any previous selected event then only clear else
       * we don't need to emit false value.
       */
      if (this.selectedKeyEvent) {
        this.clearKeyEventState();
      }
      return;
    }

    // Update with key event.
    this.selectedKeyEvent = keyEvent;

    /**
     * If mapped event is not there then it means
     * we are data is being prepared.
     */
    if (!this.mappedKeyEventsData?.length) {
      return;
    }

    const mappedKeyEvent: KeyEventData = this.mappedKeyEventsData.find((f) => f.uri === keyEvent?.uri);
    this.onKeyEventClicked(mappedKeyEvent);
  }

  // We can limit the number of series to show,
  // for that we may need to display a message with limit
  @Input()
  public seriesLimitMessage: string;

  /**
   * Input-output!!
   */
  public rangeSelection: Array<Date>;
  private rangeSelectIOModel: Array<Date>;

  @Input()
  set rangeSelectModel(val: Array<Date>) {
    if (this.rangeSelectIOModel === val) {
      return;
    }
    this.rangeSelectIOModel = val;
    /**
     *  To avoid unwanted mutation on referenced array because of d3, we copy the dates.
     *  Fail safe in the case of val is some random Array, we take current date.
     */
    this.rangeSelection = val && val.length ? val.map((date) => (date instanceof Date ? new Date(date.getTime()) : new Date())) : null;
  }

  @Input()
  set data(value: Array<TimelineData>) {
    this.timelineData = value;
    this.buildTimeline(value);
  }

  public unitTime: TimelineStep;
  @Input()
  set step(step: TimelineStep) {
    this.unitTime = step;
    this.buildTimeline(this.timelineData);
  }

  @Output()
  public rangeSelectModelChange: EventEmitter<Array<Date>> = new EventEmitter();

  @Output()
  public keyEventSelected: EventEmitter<KeyEventData | null> = new EventEmitter<KeyEventData | null>();

  /**
   * INPUTS
   */
  public actualData: Array<TimelineData>;
  public categories: Array<{
    label: string;
    id: string;
  }>;

  /**
   * SIZES
   */
  public canvas: TimelineCanvas;

  private AXIS_TICKS_HEIGHT = 5;

  /**
   * Includes the same key events data but with x and y property as well.
   */
  public mappedKeyEventsData: Array<KeyEventData> = [];

  /**
   * LINES
   */

  public chartData: Array<TimelineChartLine>;

  /**
   * AXES
   */
  public axesX: Array<TimelineAxisTick>;
  public axesY: Array<TimelineAxisTick>;

  /**
   * DATA POINTS
   */

  public dataPoints: Array<Array<TimelineDataWithXY>>;
  public animate: boolean;

  /**
   * Stores the class name for the key event we hovered.
   *
   * Note: Needed so that when the signals icon is hovered
   * we can highlight the lines as well.
   */
  public clickedKeyEventClassName: string = '';

  /**
   * Used to store the selected key event
   * and show the duration of the selected key event only.
   */
  public selectedKeyEvent: KeyEventData;

  /**
   * Stores the key event path data.
   * so that we can show gradient duration of the
   * selected key event.
   */
  public storedKeyEventPath: Record<string, keyEventPathData> = {};

  /**
   * COLORS
   */
  public colorCategoryMap: {
    [key: string]: string;
  };

  /**
   * MAPS OF DATA
   */
  public categoryMap: {
    [key: string]: Array<TimelineData>;
  };

  /**
   * RANGES
   */
  public timeRange: Array<Date>;
  public dataRange: Array<number>;
  public canvasHorizontalRange: Array<number>;
  public canvasVerticalRange: Array<number>;

  /**
   * TOOLTIP
   */

  public tooltip: TimelineTooltip;
  public tooltipWidth: number = 180;
  private tooltipAlignment: TimelineTooltipAlign;

  /**
   * Key event tooltip
   */
  public keyEventTooltip: KeyEventDataTooltip;
  private readonly keyEventTooltipWidth: number = 200;
  private readonly keyEventTooltipHeight: number = 50;
  private readonly keyEventTooltipMargin: number = 12;

  /**
   * Key events bottom circle with signal icon
   */
  public readonly circleRadius: number = 10;
  public readonly dashLineTopCircle: number = 5;

  // Bottom offset so it does not overlap line
  public readonly keyEventClassNamePrefix: string = 'key-event-';
  public readonly keyEventSignalIconCircleClassName: string = 'key-events-signal-circle';
  public readonly keyEventClassName: string = 'key-events-wrapper';
  public readonly selectedKeyEventClassName: string = 'selected-key-event';

  /**
   * Key event gradient's variables.
   */
  public linearGradientStopColor: string = '#866DF9';
  public readonly linerGradientId: string = 'liner-gradient';
  public readonly gradientWrapperId: string = 'gradient';
  public readonly keyEventDurationPathClassName: string = 'key-event-path';
  public readonly keyEventDateFormat: string = 'YYYY-MM-dd';

  /**
   * Stored the hovered key event info.
   */
  public hoveredKeyEvent: { keyEvent: KeyEventData };

  /**
   * SUBJECT
   */

  private resizeSub: Subject<void> = new Subject();

  /**
   * SUBSCRIPTIONS
   */
  private subs: Subscription = new Subscription();

  /**
   * SETTERS
   */
  private timelineData: Array<TimelineData>;

  //  FORMAT
  private formatTime = d3.timeFormat('%b %d, %y');

  /**
   * We want to close the key event when clicked elsewhere.
   *
   * @param target - Target
   */
  @HostListener('click', ['$event.target'])
  public onClick(target) {
    /**
     * If no active key events then ignore.
     *
     * If the already active key event is clicked then
     * ignore the action.
     */
    if (!this.clickedKeyEventClassName || target.classList.contains(this.clickedKeyEventClassName)) {
      return;
    }

    // Clear the key events state when clicked outside.
    this.clearKeyEventState();
  }

  /**
   * Listen resize and emit.
   *
   * @param event - Resize event.
   */
  @HostListener('window:resize', ['$event'])
  public onresize() {
    this.resizeSub.next(null);
  }

  /**
   * Listen input change
   *
   * @param changes - Change object
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.keyEventsData) {
      /**
       * KEY EVENT DATA
       */
      this.getKeyEventsData();
    }

    if (changes?.keyEventPathData) {
      /**
       * Get key event path data.
       */
      this.getKeyEventPathData();
    }
  }

  public ngOnInit() {
    //  We manage resize as subscription to be able to debounce it
    this.subs.add(
      this.resizeSub.pipe(debounceTime(100)).subscribe(() => {
        /**
         * SET CANVAS
         */
        this.setCanvas(this.host.nativeElement.offsetWidth);

        /**
         * AXES
         */
        this.axisTime();
        this.axisValue();

        /**
         * DATA POINTS
         */

        this.getDatePoints();

        /**
         * Get Key Events Data Points
         */
        this.getKeyEventsData();

        /**
         * Update key event tooltip.
         */
        this.updateKeyEventTooltipPosition();

        /**
         * Get key event path data.
         */
        this.getKeyEventPathData();
      })
    );
  }

  public ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * MOUSE EVENTS
   */

  public mouseenter(event: MouseEvent, point: TimelineData & { x: number; y: number }) {
    // If the position of the data points is close to the end move to the left
    const width = this.host.nativeElement.offsetWidth;

    this.tooltipAlignment = width - point.x < this.tooltipWidth ? TimelineTooltipAlign.LEFT : TimelineTooltipAlign.RIGHT;

    const doc = document.documentElement;
    const left = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0);
    const top = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);

    const pos = {
      x: event.pageX - left,
      y: event.pageY - top,
      marginX: this.tooltipAlignment === TimelineTooltipAlign.LEFT ? -10 : 10,
      marginY: -50,
      alignment: this.tooltipAlignment === TimelineTooltipAlign.LEFT ? -100 : 0
    };

    this.tooltip = {
      ...point,
      date: this.formatTime(new Date(`${point.date}T00:00:00.000`)),
      position: `
        translateZ(0)
        translate(${pos.x}px, ${pos.y}px)
        translate(${pos.marginX}%, ${pos.marginY}%)
        translate(${pos.alignment}%, 0px)
      `
    };
  }

  /**
   * On mouse over we want to show the key event on the top if it is
   * overlapping with some other events.
   *
   * We store the hoveredKey Event and then completely draw another
   * key event that makes it seems like it is on the top.
   *
   * We can say this is a hack for solving the z-index thing.
   * We cannot set z-index for svg in d3. So had to implement this
   *
   * @param keyEvent - Hovered key event circle with icon
   */
  public onKeyEventMouseOver(keyEvent: KeyEventData): void {
    // Store the hovered key event.
    this.hoveredKeyEvent = {
      keyEvent
    };
  }

  /**
   * On hover out we want to update the position of
   * any selected key event and clear the dummy event
   * created on hover.
   *
   * This can be the case when an overlapped event is clicked
   * and then again some other overlapped event is hovered. It will
   * push the selected key event to bottom.
   */
  public onKeyEventMouseOut(): void {
    this.hoveredKeyEvent = null;

    /**
     * If tooltip exits then update the position of selected
     * key event such that it is always appeared on the top if
     * overlapped.
     */
    if (this.keyEventTooltip) {
      const keyEventWrapper: HTMLElement = this.host.nativeElement.querySelector(
        `.${this.keyEventClassName}.${this.selectedKeyEventClassName}`
      );

      // Update to make it on top
      this.updateSelectedKeyEventPosition(keyEventWrapper);
    }
  }

  /**
   * Update the selected key event position if it is
   * overlapped with some other key event.
   *
   * @param element - Selected key event wrapper.
   */
  public updateSelectedKeyEventPosition(element: HTMLElement): void {
    // Get the parent for the key event
    const parentElement: HTMLElement = element.parentElement;

    // Hover dummy element.
    const lastChild: ChildNode = parentElement.lastChild;

    /**
     * We remove and again add at the end so that
     * it is shown above if there are any overlapping element.
     */
    element.remove();

    /**
     * We want to append the selected key event before
     * the last element else the hovered will again be
     * hidden as our dummy hover element
     * also stay as last.
     */
    parentElement.insertBefore(element, lastChild);
  }

  /**
   * Click event for the key events.
   *
   * @param point - Timeline Data for clicked item.
   */
  public onKeyEventClicked(point: KeyEventData): void {
    // If not data exits then exit.
    if (!point) {
      return;
    }

    // Store selected key event
    this.selectedKeyEvent = point;

    // Our parent may be interested in which event is selected
    this.keyEventSelected.emit(point);

    // Store the currently hovered class name
    const clickedKeyEventClassName: string = `${this.keyEventClassNamePrefix}${point.uri}`;

    // If we click multiple times in same key events we simply ignore the clicks.
    if (this.clickedKeyEventClassName === clickedKeyEventClassName) {
      return;
    }

    // Update with hovered.
    this.clickedKeyEventClassName = clickedKeyEventClassName;

    // Calculate and show key event tooltip
    this.calculateAndShowKeyEventTooltip(point);
  }

  /**
   * Clear tooltip and clear active
   * key events.
   */
  public clearKeyEventState(): void {
    this.keyEventTooltip = null;
    this.clickedKeyEventClassName = null;
    this.hoveredKeyEvent = null;
    this.keyEventSelected.emit(null);
    this.selectedKeyEvent = null;
  }

  public mouseleave() {
    this.tooltip = null;
  }

  public rangeSelectModelChangeRANGE(event) {
    this.rangeSelectModelChange.emit(this.rangeSelection);
  }

  /**
   * ANIMATIONS
   */

  public lineAnimationChanged(value: string) {
    this.animate = value === TimelineAnimationState.START;
  }

  /**
   * Create path around the line graph taking key events
   * duration.
   *
   * @param paths - Key events duration's path data in Array
   */
  private createGradientPath(keyEventUri: string, paths: Array<keyEventPathData>): void {
    const path = d3.path();
    const y = this.valueToCanvasYScale();

    // Loop through all the date data and create the paths.
    paths.forEach((p, index) => {
      if (index === 0) {
        path.moveTo(p.x, y(0));
        path.lineTo(p.x, p.y);
      } else if (index === paths.length - 1) {
        path.lineTo(p.x, p.y);
        path.lineTo(p.x, y(0));
        path.lineTo(paths[0].x, y(0));
        path.closePath();
      } else {
        path.lineTo(p.x, p.y);
      }
    });

    /**
     * Store every path record and use later to only show
     * path for selected key event only.
     */
    this.storedKeyEventPath[keyEventUri] = path;
  }

  private buildTimeline(value: Array<TimelineData>) {
    //  If there is some range available in input we want to filter the data to only work with what fits in the range
    if (this.range && value) {
      this.actualData = value.filter(
        (d) =>
          new Date(d.date).getTime() <= new Date(this.range[1]).getTime() && new Date(d.date).getTime() >= new Date(this.range[0]).getTime()
      );
    } else {
      this.actualData = value || [];
    }

    /**
     * SET CANVAS
     */
    this.setCanvas(this.host.nativeElement.offsetWidth);

    /**
     * Sort data
     */
    this.sortData();

    /**
     * Set ranges
     */

    this.getDataRange();
    this.getTimeRange();

    /**
     * Build maps
     */
    this.getCategoriesMap();
    this.initColorMap();

    /**
     * LINES
     */

    this.getChartLinesData();
    /**
     * AXES
     */
    this.axisTime();
    this.axisValue();

    /**
     * DATA POINTS & TOOLTIP
     */

    this.getDatePoints();

    // We check if the time selected fits in the new timeline if exists
    if (this.rangeSelection && this.rangeSelection.length) {
      const firstDayRangeModel = getStartOfTheDayAsDate(this.rangeSelection[0]).getTime();
      // if only range have more values, we get the next date, otherwise use firstDay itself.
      const lastDayRangeModel =
        this.rangeSelection.length > 1 ? getStartOfTheDayAsDate(this.rangeSelection[1]).getTime() : firstDayRangeModel;

      const firstDayRange = getStartOfTheDayAsDate(this.timeRange[0]).getTime();
      const lastDayRange = getStartOfTheDayAsDate(this.timeRange[1]).getTime();

      if (firstDayRangeModel < firstDayRange || lastDayRangeModel > lastDayRange) {
        this.rangeSelection = null;
      }
    }
  }

  /**
   * DATA
   * we need to sort the data
   */
  private sortData() {
    this.actualData.sort((a, b) => d3.descending(new Date(b.date), new Date(a.date)));
  }

  /**
   * CATEGORIES
   * Structure the data by categories, since we want a line for each category but the data is just a flat array
   */

  private getCategoriesMap() {
    /**
     * CATEGORY MAP
     */
    this.categoryMap = {};

    const categoriesLabelMap = {};
    this.actualData.forEach((d) => {
      this.categoryMap[d.category] = (this.categoryMap[d.category] || []).concat(d);
      categoriesLabelMap[d.category] = {
        label: d.label,
        id: d.category
      };
    });

    // we store the list of the categories here to print the legend
    this.categories = Object.keys(categoriesLabelMap)
      .map((cat, i) => ({
        id: categoriesLabelMap[cat].id,
        label: categoriesLabelMap[cat].label
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private initColorMap() {
    // If type then we have pre defined pallet for sources type.
    if (this.categoryKey === CategoryMapType.TYPE) {
      this.colorCategoryMap = this.themeService.getSignalsTypePalette();
      return;
    } else if (this.categoryKey) {
      // Random color pallet.
      this.colorCategoryMap = this.themeService.registerCategorical(
        this.categoryKey,
        this.categories.map((d) => d.id)
      );
      return;
    }
    this.colorCategoryMap = {};
    const colors = this.themeService.getDefaultPalette(ColorPalettes.Palette_CategoricalChart);
    this.categories.forEach((category, i) => {
      this.colorCategoryMap[category.id] = colors[i % colors.length];
    });
  }

  /**
   * LINE CHART
   */

  private getChartLinesData() {
    this.chartData = Object.keys(this.categoryMap).map((key) => ({
      data: this.categoryMap[key],
      category: key,
      color: this.colorCategoryMap[key]
    }));

    // Get key of chart data.
    const key: Array<string> = Object.keys(this.categoryMap);

    if (key?.length) {
      // Set the gradient color.
      this.linearGradientStopColor = this.colorCategoryMap?.[key[0]] ? this.colorCategoryMap[key[0]] : this.linearGradientStopColor;
    }
  }

  /**
   * DATA POINTS
   * circles and tooltip interaction
   */

  private getDatePoints() {
    const x = this.timeToCanvasXScale();
    const y = this.valueToCanvasYScale();

    this.dataPoints = this.chartData.map((line) =>
      line.data.map((point) => ({
        ...point,
        x: x(new Date(`${point.date}T00:00:00.000`)),
        y: y(point.value)
      }))
    );
  }

  /**
   * Key events data points.
   */
  private getKeyEventsData(): void {
    // This variables holds the functions.
    const x = this.timeToCanvasXScale();
    const y = this.valueToCanvasYScale();

    const mappedKeyEventsData: Array<KeyEventData> = this.keyEventsData?.map((d) => {
      const executionDate: Date = clearTimeAndReturnDate(d.executionDate);

      // Mapped key event
      const modifiedKeyEvent: KeyEventData = {
        ...d,
        x: x(executionDate),
        y: y(d.signalsCount)
      };

      // Return the modified key event
      return modifiedKeyEvent;
    });

    this.mappedKeyEventsData = mappedKeyEventsData;

    /**
     * Select key event according to given input.
     */
    if (this.selectedKeyEvent) {
      // Make the latest key event active by default
      this.selectKeyEventManually(this.selectedKeyEvent);
    }
  }

  /**
   * Create the path data.
   * Calculate x and y axis value.
   */
  private getKeyEventPathData(): void {
    // Clear the path before re-creating it.
    this.storedKeyEventPath = {};
    this.selectedKeyEvent = null;

    // This variables holds the functions.
    const x = this.timeToCanvasXScale();
    const y = this.valueToCanvasYScale();

    // Loop and get x, y value.
    Object.keys(this.keyEventPathData).forEach((key) => {
      const mappedPathData: Array<keyEventPathData> = this.keyEventPathData[key].map((p) => ({
        date: p.date,
        x: x(clearTimeAndReturnDate(new Date(p.date).getTime())),
        y: y(p.value),
        value: p.value
      }));

      this.createGradientPath(key, mappedPathData);
    });
  }

  /**
   * AXIS
   */
  private axisTime() {
    // If the step has not been provided
    // we set by default
    this.unitTime = this.unitTime || TimelineStep.DAY;
    // the step could be: day, moth, year...
    // each type has defined the format and the ideal width to render the label
    const unitConfig: TimelineUnitConfig = timeUnitConfig(this.unitTime);

    // We want count how many units of time
    // days, months, ... are in the date range.
    const numTimeUnits = unitConfig.count(this.timeRange);

    const formatAxis = unitConfig.formatAxis;
    const idealLabelWidth = unitConfig.width;

    const scaleTime = d3.scaleTime().domain(this.timeRange);

    // .ticks() divides the time range
    // and produces an array
    // with time information
    const ticks = scaleTime.ticks(numTimeUnits);

    // in order to calculate the coordinates where to position
    // each mark in the axis, we need a scale that converts
    // time to coordinates
    const scaleCoord = scaleTime.range(this.canvasHorizontalRange);

    //  We calculate the width of the tick in order to check if fits,
    //  if not we show label each X step
    const tickWidth = ticks.length > 1 ? scaleCoord(ticks[1]) - scaleCoord(ticks[0]) : 0;

    // We calculate when to skip the label
    const jump = Math.ceil(idealLabelWidth / tickWidth);

    this.axesX = ticks
      .filter((t, i) => i % jump === 0)
      .map((t) => ({
        coord: scaleCoord(t),
        label: formatAxis(t),
        size: this.AXIS_TICKS_HEIGHT
      }));
  }

  private axisValue() {
    // If made hidden return.
    if (!this.showYAxis) {
      return;
    }

    const scaleCoord = d3.scaleLinear().domain(this.dataRange).nice().range(this.canvasVerticalRange);
    const ticks = d3.scaleLinear().domain(this.dataRange).nice().ticks(5);

    this.axesY = ticks.map((t) => ({
      coord: scaleCoord(t),
      label: `${t}`,
      size: this.AXIS_TICKS_HEIGHT
    }));
  }

  private setCanvas(width: number) {
    const CANVAS_HEIGHT = 228;
    const LABEL_AXIS_X_WIDTH = 80;

    const MARGIN_TOP = 24;
    const MARGIN_BOTTOM = 22;
    const MARGIN_LEFT = LABEL_AXIS_X_WIDTH;
    const MARGIN_RIGHT = 20;
    const PADDING_LEFT = 30;
    const PADDING_RIGHT = 20;
    const PADDING_TOP = 0;
    const PADDING_BOTTOM = 0;

    this.canvas = {
      width,
      height: CANVAS_HEIGHT + MARGIN_TOP + MARGIN_BOTTOM,
      margin: {
        top: MARGIN_TOP,
        left: MARGIN_LEFT,
        right: MARGIN_RIGHT,
        bottom: MARGIN_BOTTOM
      },
      padding: {
        top: PADDING_TOP,
        left: PADDING_LEFT,
        right: PADDING_RIGHT,
        bottom: PADDING_BOTTOM
      }
    };

    //  Also we set the canvas ranges
    // subtract from left because date should not cut so added little more margin.
    this.canvasHorizontalRange = [this.canvas.margin.left, this.canvas.width - this.canvas.margin.left];
    this.canvasVerticalRange = [this.canvas.height - this.canvas.margin.bottom, this.canvas.margin.top];
  }

  /**
   * RANGES
   */

  private getDataRange() {
    /**
     * If normalize data range exits then use that.
     *
     * We always use same data range when showing normalized data.
     */
    if (this.normalizedDataRange) {
      this.dataRange = this.normalizedDataRange;
      return;
    }

    const range = d3.extent(this.actualData, (d) => d.value);

    //  If there is no data we want [0, 100]
    //  If is only one piece of data we set the first date to zero [0, max]
    this.dataRange = this.actualData.length > 0 ? (range[0] === range[1] ? [0, range[1]] : range) : [0, 100];
  }

  private getTimeRange() {
    /**
     * If range is selected by the user.
     *
     * We are clearing the time because
     * time was creating plotting issue.
     */
    if (this.range) {
      const startDate: Date = clearTimeAndReturnDate(new Date(this.range[0]).getTime());
      const endDate: Date = clearTimeAndReturnDate(new Date(this.range[1]).getTime());

      this.timeRange = [startDate, endDate];
      return;
    }

    this.timeRange =
      this.actualData.length > 0
        ? d3.extent(this.actualData, (d) => clearTimeAndReturnDate(new Date(d.date).getTime()))
        : [new Date(), new Date()];
  }

  /**
   * SCALES
   */

  //  TIME SCALES
  private timeToCanvasXScale() {
    return d3.scaleTime().domain(this.timeRange).range(this.canvasHorizontalRange);
  }

  // VALUE SCALES

  private valueToCanvasYScale() {
    return d3.scaleLinear().domain(this.dataRange).nice().range(this.canvasVerticalRange);
  }

  /**
   * Calculate and show the key event tooltip position.
   *
   * @param point - Key event for which the tooltip is to be shown.
   */
  private calculateAndShowKeyEventTooltip(point: KeyEventData): void {
    // The dot line
    const svg: Element = this.host.nativeElement.querySelector(`svg.svg`);
    const line: Element = this.host.nativeElement.querySelector(`.${this.clickedKeyEventClassName}`);

    /**
     * Get the x position of tooltip.
     *
     * We subtract the tooltip by 2 so that we can align tooltip at the center,
     */
    const tooltipXPosition: number = line?.getClientRects()?.[0].x - svg?.getClientRects()?.[0].x - this.keyEventTooltipWidth / 2;

    /**
     * We subtract the client rect y with the tooltip height and (gap value as a offset for some gap).
     *
     * We do this so that we can get the tooltip in exact y position.
     */
    const tooltipYPosition: number =
      line?.getClientRects()?.[0].y - svg?.getClientRects()?.[0].y - this.keyEventTooltipHeight - this.keyEventTooltipMargin;

    // Update the tooltip data
    this.keyEventTooltip = {
      data: point,
      position: `translate(${tooltipXPosition}px, ${tooltipYPosition}px)`
    };
  }

  /**
   * Updates the tooltip position on resize.
   */
  private updateKeyEventTooltipPosition(): void {
    // If any key event is selected then only.
    if (this.selectedKeyEvent) {
      // Get selected key event
      const selectedKeyEvent: KeyEventData = this.mappedKeyEventsData.find((k) => k.uri === this.selectedKeyEvent?.uri);

      /**
       * We need set timeout here because we time does not updates
       * instantly so we need to wait until timeline is updated and then
       * only show the tooltip.
       *
       * This hack works as of now.
       */
      setTimeout(() => {
        /**
         * Update tooltip position on resize
         */
        this.calculateAndShowKeyEventTooltip(selectedKeyEvent);
      }, 0);
    }
  }

  /**
   * We by default select the latest key event.
   */
  public selectKeyEventManually(keyEvent?: KeyEventData): void {
    if (this.mappedKeyEventsData?.length) {
      const mappedKeyEvent: KeyEventData = this.mappedKeyEventsData.find((f) => f.uri === keyEvent.uri);
      /**
       * We need set timeout here because we time does not updates
       * instantly so we need to wait until timeline is updated and then
       * only show the tooltip.
       *
       * This hack works as of now.
       */
      setTimeout(() => {
        this.onKeyEventClicked(mappedKeyEvent);
      }, 0);
    }
  }
}
